// ============================================================================
// MissingFeatures Component (View)
// ============================================================================

import { Box, Typography, Button, Alert, IconButton, CircularProgress } from "@mui/material";
import { Visibility, VisibilityOff, Delete as DeleteIcon, ScatterPlot, GridView } from "@mui/icons-material";
import JobMap from "../../../../../../helpers/JobMap";
import { BOUNDARY_COLORS } from "../../shared/constants";
import type { MissingFeaturesProps } from "./MissingFeatures.types";
import { useMissingFeaturesViewModel } from "./MissingFeatures.vm";
import "./MissingFeatures.css";
import { PortalTooltip } from "../../../../../../helpers";

/** Accepted file types for upload - matches GeoDataImportForm */
const ACCEPTED_FILE_TYPES = ".zip,.json,.geojson,.kml,.kmz,.shp,.shx,.dbf,.prj";

/**
 * Component for displaying and resolving missing features issues
 * (jobs with a boundary but no points or zones)
 */
export function MissingFeatures(props: MissingFeaturesProps) {
	const vm = useMissingFeaturesViewModel(props);

	// No issues state
	if (!vm.hasIssues) {
		return (
			<Box data-testid="no-issues-message" className="missing-features no-issues">
				<Alert severity="success">
					<Typography>No missing points/zones detected. You can proceed to the next step.</Typography>
				</Alert>
			</Box>
		);
	}

	// No current issue (shouldn't happen but handle gracefully)
	if (!vm.currentIssue) {
		return null;
	}

	return (
		<Box className="missing-features">
			{/* Issue Description */}
			<Box className="issue-description">
				<Typography variant="body2" color="text.secondary">
					This job has a boundary but no points or zones.
				</Typography>
			</Box>

			{/* Side-by-side layout: Content left, Map right */}
			<Box className="side-by-side-layout">
				{/* Left side - Content */}
				<Box className="left-side">
					{/* Boundary Card */}
					<Box className="boundary-card">
						{/* Header with visibility toggle */}
						<Box className="card-header">
							{/* Color Swatch */}
							<Box
								className={`color-swatch ${vm.hideBoundary ? "hidden" : ""}`}
								sx={{
									bgcolor: BOUNDARY_COLORS[0],
									borderColor: vm.hideBoundary ? undefined : BOUNDARY_COLORS[0],
								}}
							/>

							{/* Visibility Toggle */}
							<PortalTooltip title={vm.hideBoundary ? "Show on map" : "Hide on map"}>
								<IconButton size="small" onClick={vm.toggleBoundaryVisibility} className="visibility-toggle">
									{vm.hideBoundary ? (
										<VisibilityOff fontSize="small" color="disabled" />
									) : (
										<Visibility fontSize="small" color="primary" />
									)}
								</IconButton>
							</PortalTooltip>

							{/* Label */}
							<Typography variant="subtitle2" sx={{ flex: 1, opacity: vm.hideBoundary ? 0.6 : 1 }}>
								{vm.jobDisplayName}
							</Typography>
						</Box>
					</Box>

					{/* Hidden file inputs */}
					<input
						type="file"
						ref={vm.pointsFileInputRef}
						style={{ display: "none" }}
						accept={ACCEPTED_FILE_TYPES}
						multiple
						onChange={vm.handlePointsFileChange}
					/>
					<input
						type="file"
						ref={vm.zonesFileInputRef}
						style={{ display: "none" }}
						accept={ACCEPTED_FILE_TYPES}
						multiple
						onChange={vm.handleZonesFileChange}
					/>

					{/* Action buttons - single row, collapses to icon-only when narrow */}
					<Box className="action-buttons">
						{vm.canUploadPoints && (
							<PortalTooltip title="Upload Points">
								<Button
									className="action-button"
									variant="outlined"
									size="small"
									startIcon={vm.isUploadingPoints ? <CircularProgress size={16} /> : <ScatterPlot />}
									onClick={vm.handleUploadPointsClick}
									disabled={vm.isUploadingPoints || vm.isUploadingZones}
									sx={{ flex: 1 }}
								>
									<span className="button-label">Upload Points</span>
								</Button>
							</PortalTooltip>
						)}

						{vm.canUploadZones && (
							<PortalTooltip title="Upload Zones">
								<Button
									className="action-button"
									variant="outlined"
									size="small"
									startIcon={vm.isUploadingZones ? <CircularProgress size={16} /> : <GridView />}
									onClick={vm.handleUploadZonesClick}
									disabled={vm.isUploadingPoints || vm.isUploadingZones}
									sx={{ flex: 1 }}
								>
									<span className="button-label">Upload Zones</span>
								</Button>
							</PortalTooltip>
						)}

						<PortalTooltip title="Delete this job">
							<Button
								className="action-button"
								variant="outlined"
								color="error"
								size="small"
								startIcon={<DeleteIcon />}
								onClick={vm.handleDeleteJob}
								sx={{ flex: 1 }}
							>
								<span className="button-label">Delete Job</span>
							</Button>
						</PortalTooltip>
					</Box>
				</Box>

				{/* Right side - Map */}
				<Box className="right-side">
					<JobMap
						jobs={vm.hideBoundary ? [] : [vm.currentIssue.job]}
						colorMode="boundary-hatch"
						height="100%"
						gestureHandling="cooperative"
					/>
				</Box>
			</Box>
		</Box>
	);
}
